import { useRef, useState, type DragEvent } from "react";
import { Button } from "@/components/ui/button";
import { SrIcon } from "@/components/ui/SrIcon";
import SectionCard from "@/components/customers/SectionCard";
import { MentionListbox } from "@/components/project/MentionListbox";
import { useAuth } from "@/contexts/AuthContext";
import { useUsers } from "@/hooks/use-users";
import { useMentionComposer } from "@/hooks/use-mention-composer";
import {
  useCreateFreelancerComment,
  useDeleteFreelancerComment,
  useFreelancerComments,
  useFreelancerImageUrls,
  isImageAttachment,
  type FreelancerCommentRow,
} from "@/hooks/use-freelancer-comments";
import { FreelancerCommentItem } from "./FreelancerCommentItem";

/** Loggen: kommentarer med svar, @-nevning og filer. Erstatter Monday-oppdateringene. Bilder vises som miniatyrer (VIB-2934). */
export function FreelancerCommentsSection({ freelancerId }: { freelancerId: string }) {
  const { user, isAdmin } = useAuth();
  const { data: users = [] } = useUsers();
  const { data: comments = [] } = useFreelancerComments(freelancerId);
  const create = useCreateFreelancerComment();
  const remove = useDeleteFreelancerComment();
  const composer = useMentionComposer({ users, idPrefix: "freelancer-mention" });
  const [files, setFiles] = useState<File[]>([]);
  const [replyTo, setReplyTo] = useState<FreelancerCommentRow | null>(null);
  const [dragging, setDragging] = useState(false);
  const [focused, setFocused] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const textarea = useRef<HTMLTextAreaElement>(null);

  const total = comments.reduce((n, c) => n + 1 + c.replies.length, 0);
  const fromMonday = comments.reduce((n, c) => n + (c.monday_update_id ? 1 : 0) + c.replies.filter((r) => r.monday_update_id).length, 0);
  const imagePaths = comments
    .flatMap((c) => [c, ...c.replies])
    .flatMap((c) => c.attachments)
    .filter(isImageAttachment)
    .map((a) => a.storage_path as string);
  const { data: imageUrls } = useFreelancerImageUrls(freelancerId, imagePaths);
  const expanded = focused || !!composer.body || files.length > 0 || !!replyTo;

  const send = () => {
    if (!composer.body.trim() && files.length === 0) return;
    create.mutate(
      {
        freelancer_id: freelancerId,
        body: composer.body,
        mentioned_user_ids: composer.resolveMentionedIds(),
        parent_comment_id: replyTo?.id ?? null,
        files,
      },
      {
        onSuccess: () => {
          composer.reset();
          setFiles([]);
          setReplyTo(null);
        },
      },
    );
  };

  const addFiles = (list: FileList | null) => {
    // Kopier før oppdateringen: FileList er levende og tømmes når input-verdien nullstilles.
    const added = list ? Array.from(list) : [];
    if (added.length > 0) setFiles((prev) => [...prev, ...added]);
  };

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragging(false);
    addFiles(e.dataTransfer.files);
  };

  return (
    <SectionCard
      title="Logg"
      meta={total > 0 ? `${total} kommentar${total === 1 ? "" : "er"}${fromMonday > 0 ? ` · ${fromMonday} fra Monday` : ""}` : undefined}
    >
      <div
        className={`relative space-y-2 px-5 py-4 ${dragging ? "bg-primary/5" : ""}`}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
      >
        {replyTo && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            Svarer {replyTo.author?.full_name ?? replyTo.monday_author_name ?? "kommentar"}
            <button type="button" className="text-primary hover:underline" onClick={() => setReplyTo(null)}>Avbryt</button>
          </div>
        )}
        <MentionListbox composer={composer} />
        <textarea
          ref={textarea}
          aria-label="Ny kommentar"
          aria-autocomplete="list"
          aria-controls={composer.mentionsOpen ? composer.listboxId : undefined}
          aria-activedescendant={composer.highlightedUser ? composer.optionId(composer.highlightedUser.id) : undefined}
          className={`w-full rounded-md border-[1.5px] border-foreground/25 bg-card px-3 py-2 text-sm focus-visible:border-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 ${expanded ? "min-h-[64px] resize-y" : "h-10 resize-none"}`}
          rows={1}
          onFocus={() => setFocused(true)}
          placeholder="Skriv i loggen. @ for å nevne en kollega. ⌘/Ctrl+Enter sender."
          value={composer.body}
          onChange={(e) => composer.setBody(e.target.value)}
          onKeyDown={(e) => {
            if (composer.handleKeyDown(e)) return;
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              send();
            }
          }}
        />
        {files.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {files.map((f, i) => (
              <span key={`${f.name}-${i}`} className="inline-flex items-center gap-1.5 rounded-md border border-border px-2 py-1 text-xs">
                {f.name}
                <button type="button" aria-label={`Fjern ${f.name}`} onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}>
                  <SrIcon name="x" className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}
        <div className={`items-center justify-between gap-2 ${expanded ? "flex" : "hidden"}`}>
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <SrIcon name="paperclip" className="h-3.5 w-3.5" />
            Dra filer hit eller
            <button type="button" className="text-primary hover:underline" onClick={() => fileInput.current?.click()}>velg fil</button>
            · maks 25 MB
          </span>
          <input ref={fileInput} type="file" multiple className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
          <Button size="sm" onClick={send} disabled={create.isPending || (!composer.body.trim() && files.length === 0)}>
            <SrIcon name="send" className="h-4 w-4" />
            Send
          </Button>
        </div>
      </div>
      {comments.length > 0 && (
        <div className="space-y-5 border-t border-border px-5 py-4">
          {comments.map((c) => (
            <FreelancerCommentItem
              key={c.id}
              comment={c}
              users={users}
              imageUrls={imageUrls}
              canDelete={(x) => !!user && (x.author_id === user.id || isAdmin)}
              onDelete={(x) => remove.mutate({ id: x.id, freelancerId })}
              onReply={(x) => { setReplyTo(x); textarea.current?.focus(); }}
            />
          ))}
        </div>
      )}
    </SectionCard>
  );
}
